import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Warehouse, WarehouseCreate, WarehouseUpdate } from '../models/Warehouse';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';
import { PagedResult, PaginationOptions } from '../shared/interfaces/pagination.interface';

export type WarehousePagedResult = PagedResult<Warehouse>;

@Injectable({ providedIn: 'root' })
export class WarehousesService {
  private get baseUrl() { return `${environment.microservices.stock}`; }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  // CRUD básico
  getAll(options?: PaginationOptions): Observable<WarehousePagedResult> {
    let params = new HttpParams();
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    
    return this.http.get<any>(`${this.baseUrl}/warehouse`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => { 
        this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados'); 
        return throwError(() => err); 
      })
    );
  }

  getById(id: number): Observable<Warehouse> {
    return this.http.get<any>(`${this.baseUrl}/warehouse/${id}`).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifado'); return throwError(() => err); })
    );
  }

  create(payload: WarehouseCreate): Observable<Warehouse> {
    const dto = this.mapToDto(payload);
    return this.http.post<any>(`${this.baseUrl}/warehouse`, dto).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao criar almoxarifado'); return throwError(() => err); })
    );
  }

  update(id: number, payload: WarehouseUpdate): Observable<Warehouse> {
    const dto = this.mapToDto(payload);
    return this.http.put<any>(`${this.baseUrl}/warehouse/${id}`, dto).pipe(
      map(x => this.mapFromDto(x)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao atualizar almoxarifado'); return throwError(() => err); })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/warehouse/${id}`).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao excluir almoxarifado'); return throwError(() => err); })
    );
  }

  // Consultas específicas
  getByCode(code: string): Observable<Warehouse | null> {
    return this.http.get<any>(`${this.baseUrl}/warehouse/code/${encodeURIComponent(code)}`).pipe(
      map(dto => this.mapFromDto(dto)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifado por código'); return throwError(() => err); })
    );
  }

  getActive(): Observable<Warehouse[]> {
    return this.http.get<any[]>(`${this.baseUrl}/warehouse/active`).pipe(
      map(list => this.mapListFromDto(list || [])),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados ativos'); return throwError(() => err); })
    );
  }

  getByType(type: string): Observable<Warehouse[]> {
    return this.http.get<any[]>(`${this.baseUrl}/warehouse/type/${encodeURIComponent(type)}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados por tipo'); return throwError(() => err); })
    );
  }

  getByStatus(status: string): Observable<Warehouse[]> {
    return this.http.get<any[]>(`${this.baseUrl}/warehouse/status/${encodeURIComponent(status)}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados por status'); return throwError(() => err); })
    );
  }

  searchByName(name: string): Observable<Warehouse[]> {
    return this.http.get<any[]>(`${this.baseUrl}/warehouse/search/name/${encodeURIComponent(name)}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados por nome'); return throwError(() => err); })
    );
  }

  getByCategory(category: string): Observable<Warehouse[]> {
    return this.http.get<any[]>(`${this.baseUrl}/warehouse/category/${encodeURIComponent(category)}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados por categoria'); return throwError(() => err); })
    );
  }

  getByDepartment(department: string): Observable<Warehouse[]> {
    return this.http.get<any[]>(`${this.baseUrl}/warehouse/department/${encodeURIComponent(department)}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados por departamento'); return throwError(() => err); })
    );
  }

  getByBranch(branch: string): Observable<Warehouse[]> {
    return this.http.get<any[]>(`${this.baseUrl}/warehouse/branch/${encodeURIComponent(branch)}`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados por filial'); return throwError(() => err); })
    );
  }

  getMainWarehouses(): Observable<Warehouse[]> {
    return this.http.get<any[]>(`${this.baseUrl}/warehouse/main`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados principais'); return throwError(() => err); })
    );
  }

  getWarehousesRequiringAuthorization(): Observable<Warehouse[]> {
    return this.http.get<any[]>(`${this.baseUrl}/warehouse/requiring-authorization`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados que requerem autorização'); return throwError(() => err); })
    );
  }

  getWarehousesWithAvailableCapacity(): Observable<Warehouse[]> {
    return this.http.get<any[]>(`${this.baseUrl}/warehouse/available-capacity`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados com capacidade disponível'); return throwError(() => err); })
    );
  }

  getWarehousesWithAvailableArea(): Observable<Warehouse[]> {
    return this.http.get<any[]>(`${this.baseUrl}/warehouse/available-area`).pipe(
      map(list => this.mapListFromDto(list)),
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar almoxarifados com área disponível'); return throwError(() => err); })
    );
  }

  // ===== STATISTICS FROM GUIDE =====

  getTotalAreaByType(type: string): Observable<{ type: string; totalArea: number }> {
    return this.http.get<{ type: string; totalArea: number }>(`${this.baseUrl}/warehouse/statistics/total-area/${encodeURIComponent(type)}`).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar área total por tipo'); return throwError(() => err); })
    );
  }

  getTotalCapacityByType(type: string): Observable<{ type: string; totalCapacity: number }> {
    return this.http.get<{ type: string; totalCapacity: number }>(`${this.baseUrl}/warehouse/statistics/total-capacity/${encodeURIComponent(type)}`).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar capacidade total por tipo'); return throwError(() => err); })
    );
  }

  getTotalWeightByType(type: string): Observable<{ type: string; totalWeight: number }> {
    return this.http.get<{ type: string; totalWeight: number }>(`${this.baseUrl}/warehouse/statistics/total-weight/${encodeURIComponent(type)}`).pipe(
      catchError(err => { this.errorService.handleHttpError(err, 'Erro ao buscar peso total por tipo'); return throwError(() => err); })
    );
  }

  // Validações
  checkCodeExists(code: string, excludeId?: number): Observable<{ exists: boolean }> {
    // Se o código está vazio, não precisa verificar
    if (!code || code.trim() === '') {
      return of({ exists: false });
    }

    // Como não existe rota específica para check-code, vamos usar a rota GetWarehouseByCode
    // Se retornar um warehouse, significa que o código existe
    return this.http.get<any>(`${this.baseUrl}/warehouse/code/${encodeURIComponent(code.trim())}`).pipe(
      map(warehouse => {
        // Se encontrou um warehouse, verificar se é o mesmo que está sendo editado
        if (excludeId && warehouse && warehouse.id === excludeId) {
          return { exists: false }; // É o mesmo registro sendo editado
        }
        return { exists: !!warehouse }; // Se encontrou warehouse, código existe
      }),
      catchError(err => {
        // Se retornou 404 ou {"error":"Warehouse not found"}, significa que o código não existe
        if (err.status === 404) {
          return of({ exists: false });
        }
        
        // Verificar se a resposta contém a mensagem "Warehouse not found"
        if (err.error && err.error.error === "Warehouse not found") {
          return of({ exists: false });
        }
        
        // Para outros erros, não tratar como "código existe"
        return of({ exists: false });
      })
    );
  }

  // Mapeamentos DTO <-> Front
  private mapFromDto(dto: any): Warehouse {
    const pick = (o: any, keys: string[]) => keys.map(k => o?.[k]).find(v => v !== undefined);
    const toIso = (d: any) => (d ? new Date(d).toISOString() : undefined);
    return {
      id: pick(dto, ['Id', 'id']) ?? 0,
      code: pick(dto, ['Code', 'code']) ?? '',
      name: pick(dto, ['Name', 'name']) ?? '',
      description: pick(dto, ['Description', 'description']),
      address: pick(dto, ['Address', 'address']),
      city: pick(dto, ['City', 'city']),
      state: pick(dto, ['State', 'state']),
      zipCode: pick(dto, ['ZipCode', 'zipCode']),
      country: pick(dto, ['Country', 'country']),
      building: pick(dto, ['Building', 'building']),
      floor: pick(dto, ['Floor', 'floor']),
      room: pick(dto, ['Room', 'room']),
      type: pick(dto, ['Type', 'type']) ?? '',
      category: pick(dto, ['Category', 'category']),
      department: pick(dto, ['Department', 'department']),
      branch: pick(dto, ['Branch', 'branch']),
      responsiblePerson: pick(dto, ['ResponsiblePerson', 'responsiblePerson']),
      responsibleEmail: pick(dto, ['ResponsibleEmail', 'responsibleEmail']),
      responsiblePhone: pick(dto, ['ResponsiblePhone', 'responsiblePhone']),
      managerStaffId: pick(dto, ['ManagerStaffId', 'managerStaffId']),
      totalArea: pick(dto, ['TotalArea', 'totalArea']),
      usedArea: pick(dto, ['UsedArea', 'usedArea']),
      availableArea: pick(dto, ['AvailableArea', 'availableArea']),
      maxCapacity: pick(dto, ['MaxCapacity', 'maxCapacity']),
      currentCapacity: pick(dto, ['CurrentCapacity', 'currentCapacity']),
      maxWeight: pick(dto, ['MaxWeight', 'maxWeight']),
      currentWeight: pick(dto, ['CurrentWeight', 'currentWeight']),
      startDate: toIso(pick(dto, ['StartDate', 'startDate'])),
      endDate: toIso(pick(dto, ['EndDate', 'endDate'])),
      status: pick(dto, ['Status', 'status']) ?? 'Ativo',
      isActive: pick(dto, ['IsActive', 'isActive']) ?? true,
      isMainWarehouse: pick(dto, ['IsMainWarehouse', 'isMainWarehouse']) ?? false,
      requiresAuthorization: pick(dto, ['RequiresAuthorization', 'requiresAuthorization']) ?? false,
      hasSecurity: pick(dto, ['HasSecurity', 'hasSecurity']) ?? false,
      hasTemperatureControl: pick(dto, ['HasTemperatureControl', 'hasTemperatureControl']) ?? false,
      minTemperature: pick(dto, ['MinTemperature', 'minTemperature']),
      maxTemperature: pick(dto, ['MaxTemperature', 'maxTemperature']),
      hasHumidityControl: pick(dto, ['HasHumidityControl', 'hasHumidityControl']) ?? false,
      minHumidity: pick(dto, ['MinHumidity', 'minHumidity']),
      maxHumidity: pick(dto, ['MaxHumidity', 'maxHumidity']),
      warehouseGroup: pick(dto, ['WarehouseGroup', 'warehouseGroup']),
      costCenterCode: pick(dto, ['CostCenterCode', 'costCenterCode']),
      costCenterId: pick(dto, ['CostCenterId', 'costCenterId']),
      notes: pick(dto, ['Notes', 'notes']),
      createdAt: toIso(pick(dto, ['CreatedAt', 'createdAt'])),
      updatedAt: toIso(pick(dto, ['UpdatedAt', 'updatedAt'])),
      createdBy: pick(dto, ['CreatedBy', 'createdBy']),
      updatedBy: pick(dto, ['UpdatedBy', 'updatedBy']),
      managerStaffName: pick(dto, ['ManagerStaffName', 'managerStaffName']),
      costCenterName: pick(dto, ['CostCenterName', 'costCenterName']),
      costCenterCodeDisplay: pick(dto, ['CostCenterCodeDisplay', 'costCenterCodeDisplay']),
      areaUtilizationPercentage: pick(dto, ['AreaUtilizationPercentage', 'areaUtilizationPercentage']),
      capacityUtilizationPercentage: pick(dto, ['CapacityUtilizationPercentage', 'capacityUtilizationPercentage']),
      weightUtilizationPercentage: pick(dto, ['WeightUtilizationPercentage', 'weightUtilizationPercentage'])
    };
  }

  private mapListFromDto(list: any[]): Warehouse[] {
    if (!list || !Array.isArray(list)) return [];
    return list.map(x => this.mapFromDto(x));
  }

  private mapToDto(payload: Partial<WarehouseCreate | WarehouseUpdate>): any {
    return {
      Code: payload.code,
      Name: payload.name,
      Description: payload.description,
      Address: payload.address,
      City: payload.city,
      State: payload.state,
      ZipCode: payload.zipCode,
      Country: payload.country,
      Building: payload.building,
      Floor: payload.floor,
      Room: payload.room,
      Type: payload.type,
      Category: payload.category,
      Department: payload.department,
      Branch: payload.branch,
      ResponsiblePerson: payload.responsiblePerson,
      ResponsibleEmail: payload.responsibleEmail,
      ResponsiblePhone: payload.responsiblePhone,
      ManagerStaffId: payload.managerStaffId,
      TotalArea: payload.totalArea,
      UsedArea: payload.usedArea,
      AvailableArea: payload.availableArea,
      MaxCapacity: payload.maxCapacity,
      CurrentCapacity: payload.currentCapacity,
      MaxWeight: payload.maxWeight,
      CurrentWeight: payload.currentWeight,
      StartDate: payload.startDate ? new Date(payload.startDate).toISOString() : null,
      EndDate: payload.endDate ? new Date(payload.endDate).toISOString() : null,
      Status: payload.status,
      IsActive: payload.isActive,
      IsMainWarehouse: payload.isMainWarehouse,
      RequiresAuthorization: payload.requiresAuthorization,
      HasSecurity: payload.hasSecurity,
      HasTemperatureControl: payload.hasTemperatureControl,
      MinTemperature: payload.minTemperature,
      MaxTemperature: payload.maxTemperature,
      HasHumidityControl: payload.hasHumidityControl,
      MinHumidity: payload.minHumidity,
      MaxHumidity: payload.maxHumidity,
      WarehouseGroup: payload.warehouseGroup,
      CostCenterCode: payload.costCenterCode,
      CostCenterId: payload.costCenterId,
      Notes: payload.notes
    };
  }

  private mapPagedResponse(response: any): WarehousePagedResult {
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

    const items = this.mapListFromDto(rawItems);

    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? items.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? items.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages,
    };
  }
}
